// A rate limiter around the single `Office.auth.getAccessToken` call site. Office throttles an
// add-in that asks for a token too often (error 13013) and, less often, one that asks while a call
// is still running (13008). Neither is a login failure: it is Office saying "not this quickly".
//
// Measured 21–27 Aug 2026 in `hammock-web`: 2320 × 13013 across 898 sessions, 61 outlook-windows
// and 9 outlook-mac browsers. 13013 never appeared on attempt 1–3, only from 4 — we walk into the
// limit ourselves. In 730 of those 898 sessions the *first* call of a brand-new page load was
// already throttled: Office' throttle state outlives a task-pane reload while our in-memory
// attempt counter does not, so every reload immediately spent another call. Hence a gate whose
// state lives in localStorage.
//
// `bootstrapTokenRequestRef` in AuthorizationContext is not this. It is cleared in `.finally()`,
// so it merges only *overlapping* calls, while everything that goes wrong in production is
// sequential — a timer fires, then the 401 handler, then a visibility change. A promise ref cannot
// express "not before T"; that is what this module is for.
//
// **Every decision here fails open.** The worst this gate should ever cause is a slower login; a
// corrupt or absurd stored value must never keep someone out. See `normalizeSsoGateState`.

/** Persisted between page loads. Plain and serializable on purpose: it is JSON in localStorage. */
export interface SsoGateState {
    /** Epoch ms of the last call we actually made to Office. */
    lastCallAt?: number
    /** Epoch ms; do not call before this moment. */
    blockedUntil?: number
    /** Epoch ms of the last 13013/13008. Also dates `blockedUntil`, see `effectiveBlockedUntil`. */
    lastRateErrorAt?: number
    /** Consecutive rate errors. Drives the penalty ladder and the give-up ceiling. */
    rateErrorStreak: number
    /** Epoch ms this record was written. Anything older than the TTL is ignored. */
    updatedAt: number
}

// Every one of these must be a *literal* `process.env.X`: dotenv-webpack substitutes the text at
// build time and does not ship a `process` object, so `process.env[name]` would throw in the
// browser. The keys live in `webpack/.env`, which is the defaults file for every environment.
const envMs = (value: string | undefined, fallback: number): number => {
    const parsed = Number(value)
    return Number.isFinite(parsed) && parsed >= 0 ? parsed : fallback
}

/**
 * Minimum spacing between two calls. Small on purpose: it is not the throttle defence (the penalty
 * is), it is the guard against the handful of code paths that fire a second acquisition seconds
 * after the first — the 3s floor of the retry timer, the 401 path, a visibility change.
 */
export const MIN_SSO_INTERVAL_MS = envMs(process.env.SSO_MIN_INTERVAL_MS, 5_000)

/** Penalty after a 13013, escalating while the throttle keeps coming back. */
export const SSO_PENALTY_LADDER_MS = [120_000, 300_000, 600_000] as const

/** 13008 is "already in progress / called too soon" — a shorter, milder version of the same thing. */
export const SSO_SOFT_PENALTY_MS = envMs(process.env.SSO_SOFT_PENALTY_MS, 30_000)

/** Older state describes a throttle that has long since lifted; ignore it rather than obey it. */
export const SSO_STATE_TTL_MS = envMs(process.env.SSO_STATE_TTL_MS, 15 * 60_000)

/** Hard ceiling on how long a stored penalty can hold us back, whatever the stored value says. */
export const SSO_MAX_BLOCK_MS = envMs(process.env.SSO_MAX_BLOCK_MS, 15 * 60_000)

/** Consecutive rate errors after which we stop retrying by ourselves and ask the user. */
export const SSO_RATE_ERROR_CEILING = envMs(process.env.SSO_RATE_ERROR_CEILING, 6)

/** Why the gate is shut, and for how long. */
export interface SsoGateBlock {
    reason: 'min-interval' | 'penalty'
    /** How long until the gate opens. The caller must reschedule for this. */
    waitMs: number
    /** The blocking call was made in an earlier page load — the 730/898 population. */
    crossPageLoad: boolean
}

export type SsoOutcome = 'ok' | 'rate-limited' | 'soft-rate-limited' | 'other-error'

const OFFICE_THROTTLED_CODE = 13013
const OFFICE_CALLED_TOO_SOON_CODE = 13008

/**
 * Which kind of answer Office gave. Deliberately looser than `isOfficeThrottleError` in
 * `bootstrapRetry.ts`, which matches strictly on the number: a code that arrives as a string still
 * describes a throttled call, and acting on it costs us nothing while missing it costs a penalty.
 */
export const classifyOfficeError = (error: unknown): SsoOutcome => {
    const code = Number((error as { code?: unknown } | undefined)?.code)
    if (code === OFFICE_THROTTLED_CODE) {
        return 'rate-limited'
    }
    if (code === OFFICE_CALLED_TOO_SOON_CODE) {
        return 'soft-rate-limited'
    }
    return 'other-error'
}

/**
 * Turn anything that came out of storage into state we are willing to act on, or `undefined`.
 *
 * This is the fail-open boundary, and the only place that decides to trust a stored value:
 * missing or non-numeric fields, a record older than the TTL, and a record written in the future
 * (the clock moved, or another tab wrote nonsense) all yield `undefined`, which means "no state,
 * call freely".
 */
export const normalizeSsoGateState = (raw: unknown, now: number): SsoGateState | undefined => {
    if (!raw || typeof raw !== 'object') {
        return undefined
    }
    const candidate = raw as Record<string, unknown>
    const updatedAt = numberOrUndefined(candidate.updatedAt)
    if (updatedAt === undefined || updatedAt > now || now - updatedAt > SSO_STATE_TTL_MS) {
        return undefined
    }
    return {
        lastCallAt: numberOrUndefined(candidate.lastCallAt),
        blockedUntil: numberOrUndefined(candidate.blockedUntil),
        lastRateErrorAt: numberOrUndefined(candidate.lastRateErrorAt),
        rateErrorStreak: Math.max(0, numberOrUndefined(candidate.rateErrorStreak) ?? 0),
        updatedAt,
    }
}

const numberOrUndefined = (value: unknown): number | undefined => (typeof value === 'number' && Number.isFinite(value) ? value : undefined)

/**
 * The moment the penalty really lifts. A stored `blockedUntil` is only honoured when there is a
 * recent rate error to justify it, and never further out than `SSO_MAX_BLOCK_MS` after that error —
 * so no stored value, however absurd, can lock a user out for longer than that.
 */
const effectiveBlockedUntil = (state: SsoGateState, now: number): number => {
    if (state.blockedUntil === undefined || state.lastRateErrorAt === undefined) {
        return 0
    }
    if (state.lastRateErrorAt > now || now - state.lastRateErrorAt > SSO_STATE_TTL_MS) {
        return 0
    }
    return Math.min(state.blockedUntil, state.lastRateErrorAt + SSO_MAX_BLOCK_MS)
}

/**
 * Why we may not call `Office.auth.getAccessToken` right now, or `undefined` when we may.
 *
 * A block is never an ending: the caller must reschedule for `waitMs`. A gate that suppresses a
 * call without rearming a timer is an indefinite login hang, which is worse than any throttle.
 */
export const evaluateSsoGate = (state: SsoGateState | undefined, now: number, pageLoadStartedAt: number): SsoGateBlock | undefined => {
    if (!state) {
        return undefined
    }

    const crossPageLoad = state.lastCallAt !== undefined && state.lastCallAt < pageLoadStartedAt

    const blockedUntil = effectiveBlockedUntil(state, now)
    if (blockedUntil > now) {
        return { reason: 'penalty', waitMs: blockedUntil - now, crossPageLoad }
    }

    if (state.lastCallAt !== undefined && state.lastCallAt <= now && now - state.lastCallAt < MIN_SSO_INTERVAL_MS) {
        return { reason: 'min-interval', waitMs: MIN_SSO_INTERVAL_MS - (now - state.lastCallAt), crossPageLoad }
    }

    return undefined
}

/** Record that a call is being made now. Written before the call, so a reload mid-flight still sees it. */
export const recordSsoCall = (state: SsoGateState | undefined, now: number): SsoGateState => ({
    ...emptyState(now),
    ...state,
    lastCallAt: now,
    updatedAt: now,
})

/** Record how that call ended, and set the penalty a rate error earns. */
export const recordSsoOutcome = (state: SsoGateState | undefined, outcome: SsoOutcome, now: number): SsoGateState => {
    const base: SsoGateState = { ...emptyState(now), ...state, updatedAt: now }

    switch (outcome) {
        case 'ok':
            // A token is proof the throttle lifted; drop the penalty rather than sit it out.
            return { ...base, blockedUntil: undefined, lastRateErrorAt: undefined, rateErrorStreak: 0 }
        case 'rate-limited': {
            const rateErrorStreak = base.rateErrorStreak + 1
            const penalty = SSO_PENALTY_LADDER_MS[Math.min(rateErrorStreak - 1, SSO_PENALTY_LADDER_MS.length - 1)]
            return { ...base, rateErrorStreak, lastRateErrorAt: now, blockedUntil: now + penalty }
        }
        case 'soft-rate-limited':
            // 13008 lifts by itself in seconds, but it still counts towards the streak: a run of
            // them is the same "too fast" signal and should escalate if a 13013 follows.
            return { ...base, rateErrorStreak: base.rateErrorStreak + 1, lastRateErrorAt: now, blockedUntil: now + SSO_SOFT_PENALTY_MS }
        case 'other-error':
        default:
            // Not a rate problem. Leave the penalty and the streak alone and let the existing
            // bootstrap backoff decide what happens next.
            return base
    }
}

/** Whether we should stop retrying on our own and hand the decision to the user. */
export const ssoRateErrorCeilingReached = (state: SsoGateState | undefined): boolean => (state?.rateErrorStreak ?? 0) >= SSO_RATE_ERROR_CEILING

/**
 * Clear the penalty because a human asked for it. A deliberate click is exactly the signal Office'
 * throttle is meant to admit, so it may skip the penalty — but not `MIN_SSO_INTERVAL_MS`, which
 * exists to stop a double call from becoming two.
 */
export const clearSsoPenalty = (state: SsoGateState | undefined, now: number): SsoGateState => ({
    ...emptyState(now),
    ...state,
    blockedUntil: undefined,
    lastRateErrorAt: undefined,
    rateErrorStreak: 0,
    updatedAt: now,
})

const emptyState = (now: number): SsoGateState => ({ rateErrorStreak: 0, updatedAt: now })
